import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { JSON } from 'ta-json';
import { Asset } from '@ess/jg-rule-executor';
import { AssetServiceConfig } from '@src/app/configs/asset.service.config';

/**
 * Service responsible for uploading assets via POST request with form data
 * GraphQL mutation isn't used because apollo doesn't support form data by default
 * Ideally this should be replaced by a graphql mutation or a totally different upload flow
 */
@Injectable({
  providedIn: 'root',
})
export class AssetService {
  public readonly UPLOAD_URL_BLUEPRINT: string;
  public readonly UPLOAD_URL_VARIANT: string;
  public readonly UPLOAD_URL_ATTRIBUTE_VALUE: string;
  public readonly UPLOAD_URL_TEXTURE: string;
  public readonly UPLOAD_URL_RETAILER_OPTIONS: string;
  public readonly UPLOAD_URL_FEATURE_IMAGE: string;
  public readonly UPLOAD_URL_FONT: string;
  public readonly UPLOAD_URL_CONFIGURATOR_ICONS: string;
  public readonly UPLOAD_URL_CONFIGURATION: string;

  constructor(
    _config: AssetServiceConfig,
    private _httpClient: HttpClient
  ) {
    this.UPLOAD_URL_BLUEPRINT = `${_config.backendUrl}/graphql/uploadBlueprintModel`;
    this.UPLOAD_URL_VARIANT = `${_config.backendUrl}/graphql/uploadVariantModel`;
    this.UPLOAD_URL_ATTRIBUTE_VALUE = `${_config.backendUrl}/graphql/uploadAttributeValueIconModel`;
    this.UPLOAD_URL_TEXTURE = `${_config.backendUrl}/graphql/uploadTexture`;
    this.UPLOAD_URL_RETAILER_OPTIONS = `${_config.backendUrl}/graphql/uploadRetailerOptionsAsset`;
    this.UPLOAD_URL_FEATURE_IMAGE = `${_config.backendUrl}/graphql/uploadVariantFeatureImage`;
    this.UPLOAD_URL_FONT = `${_config.backendUrl}/graphql/uploadFont`;
    this.UPLOAD_URL_CONFIGURATOR_ICONS = `${_config.backendUrl}/graphql/uploadConfiguratorIcons`;
    this.UPLOAD_URL_CONFIGURATION = `${_config.backendUrl}/graphql/uploadConfigurationImage`;
  }

  /**
   * Uploads the given asset, using a path dependent on which identifier is present (variant/blueprint/other(attributeValue)
   * returns the full url.
   */
  public uploadAsset$<T>(file: File, appendAsset: Map<string, string>): Observable<string> {
    const formData = new FormData();
    formData.append('asset', file);
    // using a map to keep the type generic
    appendAsset.forEach((id: string, name: string) => formData.append(name, id));
    if (appendAsset.has('variant')) {
      if (appendAsset.has('featureImage')) {
        return this._uploadAsset$(formData, this.UPLOAD_URL_FEATURE_IMAGE);
      } else return this._uploadAsset$(formData, this.UPLOAD_URL_VARIANT);
    } else if (appendAsset.has('blueprint')) {
      return this._uploadAsset$(formData, this.UPLOAD_URL_BLUEPRINT);
    } else if (appendAsset.has('textureId')) {
      return this._uploadAsset$(formData, this.UPLOAD_URL_TEXTURE);
    } else if (appendAsset.has('fontType')) {
      return this._uploadAsset$(formData, this.UPLOAD_URL_FONT);
    } else if (appendAsset.has('iconType')) {
      return this._uploadAsset$(formData, this.UPLOAD_URL_CONFIGURATOR_ICONS);
    } else if (appendAsset.has('configuration')) {
      return this._uploadAsset$(formData, this.UPLOAD_URL_CONFIGURATION);
    } else if (appendAsset.has('retailer')) {
      return this._uploadAsset$(formData, this.UPLOAD_URL_RETAILER_OPTIONS);
    } else {
      return this._uploadAsset$(formData, this.UPLOAD_URL_ATTRIBUTE_VALUE);
    }
  }

  private _uploadAsset$(formData: FormData, url: string): Observable<string> {
    return this._httpClient.post(url, formData, {
      observe: 'response',
      withCredentials: true,
    }).pipe(
      map((response: HttpResponse<any>) => {
        if (!response.ok) {
          throwError(() => new Error('Invalid asset'));
        }
        return JSON.deserialize<string>(response.body, Asset);
      }),
      catchError(e => throwError(() => e))
    );
  }
}
